import { Injectable } from '@angular/core';
import { ConditionOperator } from '@app/core/enums/condition-operator.enum';
import { FieldConfig, FieldGroup } from '@app/core/model/other/field-config';
import { getValue, setValue } from '@app/shared/extra/utils';
import { Observable, Subject } from 'rxjs';

@Injectable()
export class FormStateService {

  private saveSubject = new Subject<{ code: string, data: Record<string, any>, root?: string, formId?: string }>();

  private loadSubject = new Subject<void>();

  private id = Math.random();

  public endLoad(): void {
    this.loadSubject.next();
  }

  /**
   * Generate update data associated with the FieldConfig or FieldGroupConfig that was updated and emit them through
   * {@see saved$} so that subscribers can make the necessary update request.
   * @param config FieldConfig or FieldGroupConfig associated with the form field that was changed.
   * @param fieldGroupCode Inject the fieldGroup code as necessary if it is different from the fieldConfig's code.
   * @param formId Code of the current form.
   * @param root Root path to the entity's data, in case of multiple entities.
   */
  public save(config: FieldConfig | FieldGroup, fieldGroupCode: string, formId: string, root?: string): void {
    const data = {};
    this.saveInObject(data, config, {customPath: root, fieldGroupCode: fieldGroupCode});
    this.saveSubject.next({code: fieldGroupCode, data, root, formId});
  }

  /**
   * Emits data associated with a form field that was changed.
   */
  public get saved$(): Observable<{ code: string, data: Record<string, any>, root?: string, formId?: string }> {
    return this.saveSubject.asObservable();
  }

  /**
   * Completes once the form has finished loading.
   */
  public get loaded$(): Observable<void> {
    return this.loadSubject.asObservable();
  }

  /**
   * Returns the stored value at the given path. Options can include a custom path to use as root.
   * @param path List of keys that compose the path.
   * @param options Optional parameters.
   */
  public getPath(path: string[], options: any = {}): any {
    return getValue(this, path, options);
  }

  /**
   * Stores the provided value at the given path. Options can include a custom path to use as root.
   * @param path List of keys that compose the path.
   * @param value Value to be stored.
   * @param options Optional parameters.
   */
  public setPath(path: string[], value: any, options: any = {}): void {
    const root = options.customPath ? this[options.customPath] : this;
    setValue(root, path, value);
  }

  /**
   * Save all the updated fields in the entity or in the properties object if it's belong to properties
   * @param propertiesObject The object where to save the fields belonging to properties
   * @param config The Field or the FieldGroup config
   * @param option Optional parameters
   */
  public saveInObject(
    propertiesObject: Record<string, unknown>,
    config: FieldConfig | FieldGroup,
    option?: { customPath?: string, fieldGroupCode?: string }
  ): void {
    if (config instanceof FieldGroup) this.saveFieldGroupInObject(propertiesObject, config, option);
    if (config instanceof FieldConfig) this.saveFieldInObject(propertiesObject, config, option);
  }

  /**
   * Save the field value in the entity or in the properties object if it's belong to properties
   * @param propertiesObject The object where to save the fields belonging to properties
   * @param fieldConfig The field config
   * @param option Optional parameters
   * @param fieldGroupCode The fieldGroup code if the fieldGroup is not single
   */
  private saveFieldInObject(
    propertiesObject: Record<string, unknown>,
    fieldConfig: FieldConfig,
    option?: { customPath?: string, fieldGroupCode?: string }
  ): void {
    const path = fieldConfig.fieldPath;
    // Retrieval of the value ignores the properties object in the form state service,
    // TODO: maybe add the properties object in form state?
    if (path.first() === 'properties') path.shift();

    const value = this.getPath([option?.fieldGroupCode || fieldConfig.code, 'value', fieldConfig.code], option);
    setValue(propertiesObject, fieldConfig.fieldPath, value);
  }

  /**
   * Save the values inside the field group in the entity or in the properties object if it's belong to properties
   * @param propertiesObject The object where to save the fields belonging to properties
   * @param fieldGroup The fieldGroup config
   * @param option Optional parameters
   */
  private saveFieldGroupInObject(
    propertiesObject: Record<string, unknown>,
    fieldGroup: FieldGroup,
    option?: { customPath?: string, fieldGroupCode?: string }
  ): void {
    fieldGroup.children.forEach((fieldConfig) => {
      // Save if the field is not computed or if it is, also has an ALWAYS condition
      const alwaysAllowEditing = !!fieldConfig.conditionsToEdit.singleOrNull(cond => cond.operator === ConditionOperator.ALWAYS);
      if (!fieldConfig.computed || alwaysAllowEditing) {
        this.saveFieldInObject(propertiesObject, fieldConfig, option);
      }
    });
  }

  public cleanUp(): void {
    // remove properties added by fieldBuilders (except saveSubject and loadSubject)
    Object.keys(this).forEach((key) => {
      if (!['loadSubject', 'saveSubject'].includes(key)) {
        delete this[key];
      }
    });
  }
}
